import React, { useEffect, useState } from 'react'

import { Interpolation } from '@emotion/react'

import { Dayjs } from 'dayjs'

import DatePicker from './DatePicker'

import TimePicker from './TimePicker'
import { Box } from 'front-end/src/components'
import dayjs from 'shared/dayjs'
import { isPastDate, getDateFormatBasedOnLocale } from './dateTimePickersUtils'
import { css, cx } from '@emotion/css'
import { dayjsTz } from 'front-end/src/utils/dateHelpers'
import { isIE } from 'front-end/../shared/helpers'

interface DateTimePickersProps {
  id?: string
  value: Dayjs | null
  label?: string
  variant?: 'light'
  labelStyle?: Interpolation<{}>
  disablePastDays?: boolean
  timezone?: string | null
  isValidDate?: (m: Dayjs) => boolean // date for which we return false will be disabled
  onChange?: (m: Dayjs | null) => boolean | void
  datePlaceholder?: string
  timePlaceholder?: string
  fullWidth?: boolean
  isUtc?: boolean
}

interface DateTimePickersState {
  date: string
  time: string
}

interface MakeDayJsPayload {
  newValue: string | Dayjs
  timezone?: string | null
  isUtc?: boolean
}

function makeDayJs({ newValue, timezone, isUtc }: MakeDayJsPayload) {
  return isUtc
    ? dayjs.utc(newValue)
    : dayjsTz(newValue, timezone ?? isIE ? 'UTC' : dayjs.tz.guess(), true)
}

export const DateTimePickers: React.FC<
  DateTimePickersProps & { dataTest?: string }
> = ({
  id,
  label,
  isValidDate,
  disablePastDays,
  fullWidth = false,
  value,
  timezone,
  variant,
  labelStyle,
  onChange,
  timePlaceholder,
  datePlaceholder,
  isUtc = false,
  dataTest = 'DatePicker'
}) => {
  const dateFormat = getDateFormatBasedOnLocale()
  const [state, setState] = useState<DateTimePickersState>({
    date: '',
    time: ''
  })
  const { date, time } = state

  useEffect(() => {
    // we should have an option to reset values
    setState({
      date: value ? value.format(dateFormat) : '',
      time: value ? value.format('HH:mm') : ''
    })
  }, [value])

  const onValueChange = (val: Dayjs | string) => {
    if (typeof val !== 'string' && val.isValid()) {
      val = makeDayJs({ newValue: val, timezone, isUtc })
      onChange && onChange(val)
    } else {
      onChange && onChange(null)
    }
  }

  return (
    <Box
      d="flex"
      alignItems="center"
      textAlign="left"
      pos="relative"
      paddingTop={label ? '24px' : '0px'}
      maxWidth={fullWidth ? 'none' : '400px'}
      w="100%"
      data-test={dataTest}
    >
      {label && (
        <Box
          as="span"
          css={labelStyle}
          textAlign="left"
          fontSize="var(--chakra-fontSizes-md)"
          mb="4px"
          position="absolute"
          top={0}
          left={0}
          w="100%"
        >
          {label}
        </Box>
      )}
      <DatePicker
        inputProps={{ id: id, placeholder: datePlaceholder }}
        disablePastDays={disablePastDays}
        fullWidth={fullWidth}
        // @ts-expect-error
        styles={{
          menu: (provided) => ({ ...provided, zIndex: 9999 })
        }}
        className={cx(
          variant === 'light' &&
            css`
              input {
                background-color: #ffffff !important;
                &:focus {
                  border-color: var(--chakra-colors-buttonPrimary-500);
                }
              }
            `
        )}
        utc={isUtc}
        timeFormat={false}
        dateFormat={dateFormat}
        closeOnSelect
        value={date}
        isValidDate={(maybeDate) => {
          if (disablePastDays && isPastDate(maybeDate)) {
            return false
          }
          if (!isValidDate) {
            return true
          }
          return isValidDate(maybeDate)
        }}
        onChange={(newValue) => {
          let newDayjs = makeDayJs({
            newValue: newValue.toString(),
            timezone,
            isUtc
          })
          if (!time) {
            newDayjs = newDayjs.hour(8)
          } else {
            const [hours, minutes] = time.split(':').map((n) => Number(n))
            newDayjs = newDayjs.hour(hours).minute(minutes)
          }
          if (newDayjs.isValid()) {
            onValueChange(newDayjs)
          } else {
            onValueChange(newValue.toString())
          }
        }}
      />
      <TimePicker
        value={time}
        placeholder={timePlaceholder}
        onChangeHandler={(newTime: string) => {
          setState({ ...state, time: newTime })
          if (value) {
            const newDate = isUtc ? value.utc() : value
            const dateShifted = newDate
              .hour(Number(newTime.split(':')[0]))
              .minute(Number(newTime.split(':')[1]))

            onValueChange(dateShifted)
          }
        }}
        flex={2}
        w="auto"
        minWidth="90px"
      />
    </Box>
  )
}

export default DateTimePickers
